"use client"

import styled from "@emotion/styled"
import { GlobalStyles, Typography } from "@mui/material"
import { useLocale } from "next-intl"
import React from "react"
import { sharedColors } from "./theme/colors"

const PRIVACY_URL = "https://policies.google.com/privacy"
const TERMS_URL = "https://policies.google.com/terms"

const translations = {
  he: {
    before: "אתר זה מוגן באמצעות reCAPTCHA, וחלים עליו ",
    privacy: "מדיניות הפרטיות",
    between: " ו",
    terms: "תנאי השירות",
    after: " של Google.",
    privacyAriaLabel: "מדיניות הפרטיות של Google (נפתח בחלון חדש)",
    termsAriaLabel: "תנאי השירות של Google (נפתח בחלון חדש)",
  },
  en: {
    before: "This site is protected by reCAPTCHA and the Google ",
    privacy: "Privacy Policy",
    between: " and ",
    terms: "Terms of Service",
    after: " apply.",
    privacyAriaLabel: "Google Privacy Policy (opens in a new window)",
    termsAriaLabel: "Google Terms of Service (opens in a new window)",
  },
}

const StyledText = styled(Typography)<{ component?: React.ElementType }>`
  color: ${sharedColors.grey};
  font-size: 12px;
  line-height: 140%;
  text-align: start;
  width: 100%;
`

const StyledExternalLink = styled.a`
  color: ${sharedColors.grey};
  text-decoration: underline;
  :focus-visible {
    outline-offset: 2px;
    outline: 2px solid ${sharedColors.black};
  }
`

/**
 * Google requires this notice whenever the reCAPTCHA v3 badge is hidden, so
 * the notice owns the hiding: the badge stays visible on any page that does
 * not render this. Put it at the bottom of every form that calls
 * getReCaptchaToken(). Renders nothing when reCAPTCHA is not configured —
 * without a site key nothing is loaded, and the notice would be a false claim.
 */
export default function ReCaptchaDisclaimer() {
  const locale = useLocale()
  const t = translations[locale as keyof typeof translations] || translations.he

  if (!process.env.RECAPTCHA_KEY) return null

  return (
    <>
      {/* visibility (not display) keeps the widget functional */}
      <GlobalStyles styles={{ ".grecaptcha-badge": { visibility: "hidden" } }} />
      <StyledText variant="body3" component="p">
        {t.before}
        <StyledExternalLink
          href={PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t.privacyAriaLabel}
        >
          {t.privacy}
        </StyledExternalLink>
        {t.between}
        <StyledExternalLink
          href={TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t.termsAriaLabel}
        >
          {t.terms}
        </StyledExternalLink>
        {t.after}
      </StyledText>
    </>
  )
}
